import React, { useState, useEffect } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { LazyImage, preloadImages } from '../utils/performance-optimizations';
import Loading from '../components/Loading';
import styles from './Sponsors.module.css';
import '../components/LazyImage.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

// Exhibitor & sponsor data — sourced from "Exhibitors and Sponsor list for
// CM App.xlsx" and "Floorplan with stands allocated - Conference App.pdf".
// `stand` is the Refectory South exhibition hall stand number shown on the
// floor plan; sponsors without a stand (supplier-strand/badge sponsors) have
// `stand: null`. `filename: null` means no logo file exists yet.
const SPONSOR_DATA = [
  { name: 'Liberator Ltd', filename: 'Liberator Ltd.jpg', url: 'https://www.liberator.co.uk', tier: 'Gold Exhibitor', stand: 10 },
  { name: 'Smartbox Assistive Technology', filename: 'Smartbox.jpg', url: 'https://www.smartboxat.com', tier: 'Gold Exhibitor', stand: 14, note: '40th Conference Dinner Sponsorship' },
  { name: 'Tobii Dynavox', filename: 'Tobii Dynavox.jpg', url: 'https://www.tobiidynavox.com', tier: 'Gold Exhibitor', stand: 7, note: 'Supplier Strand' },
  { name: 'Acapela Group', filename: 'Acapela Group.jpg', url: 'https://www.acapela-group.com', tier: 'Silver Exhibitor', stand: 9 },
  { name: 'Jabbla UK Ltd', filename: 'Jabbla UK Ltd.jpg', url: 'https://www.jabbla.co.uk', tier: 'Silver Exhibitor', stand: 13 },
  { name: 'Mounts & More Ltd', filename: 'Mounts & More Ltd.jpg', url: 'https://www.aacmounts.com/mmweb/', tier: 'Silver Exhibitor', stand: 11 },
  { name: 'Rehadapt', filename: 'Rehadapt.png', url: 'https://rehadapt.com', tier: 'Silver Exhibitor', stand: 3 },
  { name: 'Widgit Software', filename: 'Widgit.jpg', url: 'https://www.widgit.com', tier: 'Silver Exhibitor', stand: 8 },
  { name: 'Ace Centre', filename: 'Ace Centre.jpg', url: 'https://www.acecentre.org.uk', tier: 'Bronze Exhibitor', stand: 4 },
  { name: 'CALL Scotland', filename: 'CallScotlandLogo.png', url: 'https://www.callscotland.org.uk', tier: 'Bronze Exhibitor', stand: 2 },
  { name: 'CandLE', filename: 'CandLE.jpg', url: 'https://candleaac.com', tier: 'Bronze Exhibitor', stand: 16 },
  { name: 'Communication Matters', filename: 'Communication Matters.jpg', url: 'https://www.communicationmatters.org.uk', tier: 'Bronze Exhibitor', stand: 12 },
  { name: 'Communication Matters (Peer Support and Mentoring Project)', filename: 'Communication Matters.jpg', url: 'https://www.communicationmatters.org.uk', tier: 'Bronze Exhibitor', stand: 18 },
  { name: 'Dad In A Shed', filename: 'Dad In A Shed.png', url: 'https://dadinashed.com', tier: 'Bronze Exhibitor', stand: 15 },
  { name: 'Kerbcut', filename: 'Kerbcut.png', url: 'https://kerbcut.com', tier: 'Bronze Exhibitor', stand: 5, note: 'Delegate Bag Sponsor' },
  { name: 'National Institute of Advanced Industrial Science and Technology (AIST)', filename: 'AIST.jpg', url: 'https://www.aist.go.jp/index_en.html', tier: 'Bronze Exhibitor', stand: 17 },
  { name: 'Smile Smart Technology', filename: 'Smile Smart Technology.jpg', url: 'https://www.smilesmart-tech.com', tier: 'Bronze Exhibitor', stand: 6 },
  { name: 'Talking Mats', filename: 'Talking Mats.png', url: 'https://www.talkingmats.com', tier: 'Bronze Exhibitor', stand: 1 },
  { name: 'AssistiveWare', filename: 'AssistiveWare.png', url: 'https://www.assistiveware.com', tier: 'Sponsor', stand: null, note: 'Supplier Strand' },
  { name: 'Saltroad', filename: 'Saltroad.png', url: 'https://www.saltroad.com', tier: 'Sponsor', stand: null, note: 'Delegate Badge Sponsor' },
];

const TIER_ORDER = ['Gold Exhibitor', 'Silver Exhibitor', 'Bronze Exhibitor', 'Sponsor'];
const TIER_ICON = { 'Gold Exhibitor': '🥇', 'Silver Exhibitor': '🥈', 'Bronze Exhibitor': '🥉', Sponsor: '🤝' };

// Logos that render better as a CSS background-image than an <img> (odd
// aspect ratios / heavy whitespace in the source file).
const BACKGROUND_LOGO_NAMES = new Set(['Communication Matters', 'Communication Matters (Peer Support and Mentoring Project)', 'Smile Smart Technology', 'CandLE']);

const initials = (name) => name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const Sponsors = () => {
  const { focusFirstHeading, announce } = useAccessibility();
  useFocusableNavigation();
  const [loading, setLoading] = useState(true);
  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Preload sponsor logos that actually exist for better slideshow performance
  useEffect(() => {
    const imageSources = SPONSOR_DATA.filter((s) => s.filename).map((s) => `/Sponsors/${s.filename}`);
    preloadImages(imageSources).then(() => {
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  // Auto-advance slideshow every 5 seconds
  useEffect(() => {
    if (isPlaying && SPONSOR_DATA.length > 1) {
      const interval = setInterval(() => {
        setCurrentSponsorIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % SPONSOR_DATA.length;
          return nextIndex;
        });
      }, 5000); // 5 seconds

      return () => clearInterval(interval);
    }
    return undefined;
  }, [isPlaying]);



  useEffect(() => {
    focusFirstHeading();
    announce('Sponsors page loaded with automatic slideshow');
  }, [focusFirstHeading, announce]);

  const handleSponsorClick = (sponsor) => {
    if (sponsor.url) {
      window.open(sponsor.url, '_blank', 'noopener,noreferrer');
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const goToSponsor = (index) => {
    setCurrentSponsorIndex(index);
  };

  const goToPrevious = () => {
    const prevIndex = currentSponsorIndex === 0 ? SPONSOR_DATA.length - 1 : currentSponsorIndex - 1;
    setCurrentSponsorIndex(prevIndex);
  };

  const goToNext = () => {
    const nextIndex = (currentSponsorIndex + 1) % SPONSOR_DATA.length;
    setCurrentSponsorIndex(nextIndex);
  };

  if (loading) {
    return <Loading message="Loading Sponsors..." />;
  }

  const currentSponsor = SPONSOR_DATA[currentSponsorIndex];

  return (
    <div className={styles.sponsorsPage} data-testid="sponsors-page">
      <div className="container">
        <header className={styles.pageHeader}>
          <h1>Our Sponsors &amp; Exhibitors</h1>
          <p className={styles.pageDescription}>
            Thank you to our amazing sponsors and exhibitors who make this conference possible!
          </p>
        </header>

        {/* Sponsor Slideshow */}
        <section className={styles.advertSection} data-testid="sponsors-list">
          <div className={styles.advertContainer}>
            <h2 className={styles.featuredTitle} data-testid="sponsor-categories">
              🌟 Our Amazing Sponsors ({SPONSOR_DATA.length})
            </h2>

            <div
              className={styles.slideshowContainer}
              onFocus={() => setIsPlaying(false)}
            >
              {/* Current Sponsor Display */}
              <div className={styles.slideshowWrapper}>
                <button
                  className={styles.navButton + ' ' + styles.prevButton}
                  onClick={goToPrevious}
                  aria-label="Previous sponsor"
                  type="button"
                >
                  ‹
                </button>
                <div
                  className={styles.sponsorDisplay}
                  onClick={() => handleSponsorClick(currentSponsor)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSponsorClick(currentSponsor);
                    }
                  }}
                  aria-label={currentSponsor.url ? `Visit ${currentSponsor.name} website` : currentSponsor.name}
                >
                  {!currentSponsor.filename ? (
                    <div className={styles.logoFallback}>
                      <span className={styles.fallbackIcon} aria-hidden="true">{initials(currentSponsor.name)}</span>
                      <span className={styles.fallbackText}>{currentSponsor.name}</span>
                    </div>
                  ) : BACKGROUND_LOGO_NAMES.has(currentSponsor.name) ? (
                    <div
                      className={styles.backgroundLogoContainer}
                      title={`${currentSponsor.name} logo`}
                      ref={(el) => {
                        if (el) {
                          const imageUrl = `/Sponsors/${currentSponsor.filename}`;
                          el.style.backgroundImage = `url("${imageUrl}")`;
                        }
                      }}
                    >
                    </div>
                  ) : (
                    <LazyImage
                      src={`/Sponsors/${currentSponsor.filename}`}
                      alt={`${currentSponsor.name} logo`}
                      className={styles.advertImage}
                      priority={true}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 60vw"
                      placeholder={
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <div>Loading {currentSponsor.name}...</div>
                        </div>
                      }
                      onError={() => {}}
                      onLoad={() => {}}
                    />
                  )}
                </div>
                <button
                  className={styles.navButton + ' ' + styles.nextButton}
                  onClick={goToNext}
                  aria-label="Next sponsor"
                  type="button"
                >
                  ›
                </button>
              </div>

              {/* Slideshow Controls */}
              <div className={styles.slideshowControls}>
                <div className={styles.playbackControls}>
                  <button
                    className={styles.playPauseBtn}
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
                  >
                    {isPlaying ? '⏸️' : '▶️'}
                  </button>
                  <span className={styles.slideCounter}>
                    {currentSponsorIndex + 1} of {SPONSOR_DATA.length}
                  </span>
                </div>

                <div className={styles.slideshowDots}>
                  {SPONSOR_DATA.map((sponsor, index) => (
                    <button
                      key={`dot-${sponsor.name}`}
                      className={`${styles.dot} ${index === currentSponsorIndex ? styles.activeDot : ''}`}
                      onClick={() => goToSponsor(index)}
                      aria-label={`View ${sponsor.name}`}
                      title={sponsor.name}
                      data-sponsor={sponsor.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            <p className={styles.featuredDescription}>
              Supporting innovation in AAC technology and communication accessibility.
              <br />
              <small>Slideshow automatically advances every 5 seconds • Click any logo to visit their website</small>
            </p>
          </div>
        </section>

        {/* Full exhibitor list, grouped by sponsorship tier */}
        <section aria-labelledby="tiers-title">
          <h2 id="tiers-title" className="sr-only">Exhibitors by Sponsorship Tier</h2>
          <div className={styles.sponsorsTiers}>
            {TIER_ORDER.map((tier) => {
              const tierSponsors = SPONSOR_DATA.filter((s) => s.tier === tier);
              if (tierSponsors.length === 0) return null;
              return (
                <div key={tier} className={styles.sponsorTier}>
                  <div className={styles.tierHeader}>
                    <div className={styles.tierTitle}>
                      <span className={styles.tierIcon} aria-hidden="true">{TIER_ICON[tier]}</span>
                      {tier === 'Sponsor' ? 'Supporting Sponsors' : tier + 's'}
                    </div>
                    <span className={styles.tierCount}>{tierSponsors.length} {tierSponsors.length === 1 ? 'organisation' : 'organisations'}</span>
                  </div>
                  <div className={`${styles.sponsorsGrid} ${styles.small}`}>
                    {tierSponsors.map((sponsor) => (
                      <div key={sponsor.name} className={styles.sponsorCard}>
                        <div className={styles.sponsorContent}>
                          <div className={styles.sponsorLogo}>
                            {sponsor.filename ? (
                              <img src={`/Sponsors/${sponsor.filename}`} alt={`${sponsor.name} logo`} loading="lazy" />
                            ) : (
                              <div className={styles.logoFallback}>
                                <span className={styles.fallbackIcon} aria-hidden="true">{initials(sponsor.name)}</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.sponsorInfo}>
                            <h3 className={styles.sponsorName}>{sponsor.name}</h3>
                            {sponsor.stand && (
                              <p className={styles.sponsorTagline}>Stand {sponsor.stand}</p>
                            )}
                            {sponsor.note && (
                              <p className={styles.sponsorDescription}>{sponsor.note}</p>
                            )}
                          </div>
                          {sponsor.url && (
                            <div className={styles.sponsorActions}>
                              <a
                                href={sponsor.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline"
                              >
                                Visit Website
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Exhibition Hall Floor Plan */}
        <section className={styles.floorPlanSection} aria-labelledby="floorplan-title">
          <h2 id="floorplan-title">🗺️ Exhibition Floor Plan — Refectory South</h2>
          <p className={styles.pageDescription}>
            Find each exhibitor&apos;s stand in the Refectory South exhibition hall.
          </p>
          <div className={styles.floorPlanImageWrapper}>
            <img
              src="/exhibitor-floorplan.png"
              alt="Refectory South exhibition hall floor plan showing the layout of numbered exhibitor stands 1 to 18, catering points, and access ramps"
              className={styles.floorPlanImage}
              loading="lazy"
            />
          </div>
          <h3 className={styles.floorPlanListTitle}>Stand Directory</h3>
          <ul className={styles.floorPlanList}>
            {SPONSOR_DATA
              .filter((s) => s.stand)
              .sort((a, b) => a.stand - b.stand)
              .map((s) => (
                <li key={s.name} className={styles.floorPlanListItem}>
                  <span className={styles.floorPlanStandNumber} aria-hidden="true">{s.stand}</span>
                  <span>{s.name}</span>
                </li>
              ))}
          </ul>
        </section>

        {/* Thank You Section */}
        <section className={styles.thankYouSection}>
          <div className={styles.thankYouCard}>
            <h2>🙏 Thank You to Our Sponsors</h2>
            <p>
              We are grateful for the continued support from our sponsors who help make
              Communication Matters conferences accessible and impactful for the AAC community.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Sponsors;
